/**
 * Pure decision logic for the 通知書/合格書 certificate upload warning
 * (IROHANA_MATCH_DEV-330).
 *
 * This module intentionally has no React / service imports so the decision function
 * can be unit-tested without dragging in the app's transitive module graph.
 */

import { NOTIFICATION_KEYWORD } from "@/features/talent/constants/certificate-warning";

export type TOcrCacheEntry = {
  loading: boolean;
  /** `null` means not yet fetched or failed silently. Empty string = OCR returned no text. */
  text: string | null;
};

export type TWarningState = {
  /** True while the OCR call is in flight for this file. */
  loading: boolean;
  /** "アップロードされたファイルは合格書ではなく通知書です" should be shown. */
  msg1: boolean;
  /** "正式な合格書をアップロードしてください" should additionally be shown. */
  msg2: boolean;
};

export const IDLE_STATE: TWarningState = { loading: false, msg1: false, msg2: false };

/**
 * Pure decision function for per-file warning state.
 *
 * Inputs:
 * - `cacheEntry`: current OCR result for this file, or undefined if not yet attempted.
 * - `selectedTypeId`: document-type id currently selected for this file (from form state).
 * - `targetTypeId`: resolved id of the 「特定技能の合格書」 attachment type.
 * - `isTargetIndustry`: true iff the talent's industry is in INDUSTRIES_REQUIRING_FORMAL_CERTIFICATE.
 *
 * Two warning paths:
 * 1. type = 合格書 + OCR detects 通知書 → msg1 (+ msg2 if target industry)
 * 2. type ≠ 合格書 (but user already chose a type) + target industry → msg2 only
 *    (rationale: 3 regulated industries must upload formal 合格書; any other tagged
 *    type means user is not providing it, so prompt them.)
 */
export const computeWarning = (
  cacheEntry: TOcrCacheEntry | undefined,
  selectedTypeId: string | number | null | undefined,
  targetTypeId: number | string | null | undefined,
  isTargetIndustry: boolean,
): TWarningState => {
  if (!targetTypeId) return IDLE_STATE;
  const typeSelected = selectedTypeId !== undefined && selectedTypeId !== null && selectedTypeId !== "";
  const typeMatches = typeSelected && String(selectedTypeId) === String(targetTypeId);

  // Path 2: target industry + a non-合格書 type explicitly chosen → msg2 only.
  // Skip when type is empty (user hasn't chosen yet — avoid premature warning).
  if (isTargetIndustry && typeSelected && !typeMatches) {
    return { loading: false, msg1: false, msg2: true };
  }

  // Path 1: type = 合格書 + OCR check.
  if (!typeMatches) return IDLE_STATE;
  if (!cacheEntry) return IDLE_STATE;
  if (cacheEntry.loading) return { loading: true, msg1: false, msg2: false };
  const hasKeyword = !!cacheEntry.text && cacheEntry.text.includes(NOTIFICATION_KEYWORD);
  if (!hasKeyword) return IDLE_STATE;
  return { loading: false, msg1: true, msg2: isTargetIndustry };
};
